import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listSiteContent, absoluteUrl } from "@/lib/api";
import { SocialIcon, getPlatform } from "@/lib/socials";
import { useSettings } from "@/context/SettingsContext";

const DEFAULT_LOGO_URL =
  "/nelum-logo-dark.png";

const DEFAULT_BODY =
  "Modern Sri Lankan cuisine in the heart of Senopati. A story of spice, coast, and memory — served slowly, and together.";
const DEFAULT_TAGLINE = "Taste the Soul of Sri Lanka";
const DEFAULT_COPYRIGHT = "Nelum Dining · Jakarta";

export default function Footer() {
  const [contact, setContact] = useState({});
  const [location, setLocation] = useState({});
  const [footer, setFooter] = useState({});
  const { settings } = useSettings();
  const tint = settings?.logo_tint !== false;
  const logoUrl = settings?.logo_url
    ? (absoluteUrl(settings.logo_url) || DEFAULT_LOGO_URL)
    : DEFAULT_LOGO_URL;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const items = await listSiteContent();
        if (cancelled) return;
        setContact(items.find((c) => c.section === "contact-info") || {});
        setLocation(items.find((c) => c.section === "location-info") || {});
        setFooter(items.find((c) => c.section === "footer") || {});
      } catch { /* no-op */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const activeSocials = (contact.socials || [])
    .filter((s) => s.is_active && s.url)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const hours = Array.isArray(location.hours) ? location.hours : [];

  const body = footer.body || DEFAULT_BODY;
  const tagline = footer.closing_quote || DEFAULT_TAGLINE;
  const copyright = footer.copyright_text || DEFAULT_COPYRIGHT;
  const partnerLogos = Array.isArray(footer.partner_logos) ? footer.partner_logos : [];

  return (
    <footer
      data-testid="site-footer"
      className="border-t border-border bg-background text-foreground/80"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-6 lg:px-12 py-10 sm:py-14 lg:py-20 grid grid-cols-2 gap-x-6 gap-y-9 lg:grid-cols-12 lg:gap-14">
        <div className="col-span-2 lg:col-span-5">
          {tint ? (
            <span
              aria-hidden="true"
              data-testid="footer-logo"
              className="h-20 w-[132px] sm:h-24 sm:w-[154px] lg:h-28 lg:w-[176px] inline-block -ml-2 drop-shadow-[0_2px_16px_rgba(0,0,0,0.7)]"
              style={{
                backgroundColor: "hsl(var(--primary))",
                WebkitMaskImage: `url(${logoUrl})`,
                maskImage: `url(${logoUrl})`,
                WebkitMaskRepeat: "no-repeat",
                maskRepeat: "no-repeat",
                WebkitMaskPosition: "left center",
                maskPosition: "left center",
                WebkitMaskSize: "contain",
                maskSize: "contain",
              }}
            />
          ) : (
            <img
              src={logoUrl}
              alt="Site logo"
              data-testid="footer-logo"
              className="h-20 w-[132px] sm:h-24 sm:w-[154px] lg:h-28 lg:w-[176px] -ml-2 object-contain object-left drop-shadow-[0_2px_16px_rgba(0,0,0,0.7)]"
            />
          )}
          <p
            data-testid="footer-body"
            className="mt-3 sm:mt-5 lg:mt-6 text-sm leading-relaxed max-w-sm text-muted-foreground whitespace-pre-line"
          >
            {body}
          </p>
          <div className="mt-5 sm:mt-7 lg:mt-8 flex items-center flex-wrap gap-4">
            {activeSocials.length === 0 ? (
              <span className="text-xs text-muted-foreground italic">
                Social links appear here once enabled in the admin.
              </span>
            ) : (
              activeSocials.map((s) => {
                const p = getPlatform(s.platform);
                return (
                  <a
                    key={s.id || `${s.platform}-${s.url}`}
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`footer-social-${s.platform}`}
                    className="text-foreground/70 hover:text-primary transition-colors"
                    aria-label={p.label}
                  >
                    <SocialIcon platform={s.platform} size={18} />
                  </a>
                );
              })
            )}
          </div>
        </div>

        <div className="col-span-1 lg:col-span-3">
          <div className="overline">Visit</div>
          <div
            data-testid="footer-address"
            className="mt-3 sm:mt-5 text-sm leading-relaxed text-foreground/85"
          >
            {contact.address_line1 && (
              <>
                {contact.address_line1}
                <br />
              </>
            )}
            {contact.address_line2 && (
              <>{contact.address_line2}</>
            )}
            {!contact.address_line1 && !contact.address_line2 && (
              <span className="text-muted-foreground italic">
                Set the address in Admin → Content → Contact.
              </span>
            )}
          </div>
        </div>

        <div className="col-span-2 order-3 lg:order-none lg:col-span-2">
          <div className="overline">Hours</div>
          <div
            data-testid="footer-hours"
            className="mt-3 sm:mt-5 grid grid-cols-2 sm:grid-cols-3 lg:block text-sm leading-relaxed text-foreground/85 gap-x-5 gap-y-2 lg:space-y-0.5"
          >
            {hours.length === 0 ? (
              <span className="text-muted-foreground italic">
                Set hours in Admin → Content → Location.
              </span>
            ) : (
              hours.map((h, i) => {
                const isClosed = /closed/i.test(h.time || "");
                return (
                  <div key={`${h.day}-${i}`} className="flex flex-col min-w-0">
                    <span className="text-foreground/85 text-xs sm:text-sm truncate">{h.day}</span>
                    <span
                      className={
                        isClosed
                          ? "text-muted-foreground italic text-xs"
                          : "text-foreground/60 text-xs"
                      }
                    >
                      {h.time}
                    </span>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="col-span-1 lg:col-span-2">
          <div className="overline">Explore</div>
          <ul className="mt-3 sm:mt-5 space-y-2 sm:space-y-3 text-sm">
            {[
              ["/menus", "Menus", "footer-link-menus"],
              ["/location", "Location", "footer-link-location"],
              ["/reservation", "Reservation", "footer-link-reservation"],
              ["/contact", "Contact", "footer-link-contact"],
            ].map(([to, label, tid]) => (
              <li key={to}>
                <Link
                  to={to}
                  data-testid={tid}
                  className="link-underline text-foreground/85 hover:text-primary transition-colors"
                >
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {partnerLogos.length > 0 && (
        <div className="border-t border-border" data-testid="footer-partners">
          <div className="mx-auto max-w-[1400px] px-5 sm:px-6 lg:px-12 py-6 sm:py-8 lg:py-10">
            <div className="overline text-center text-muted-foreground/80 mb-4 sm:mb-6">
              Featured / Partners
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-7 sm:gap-x-12 gap-y-4 sm:gap-y-6">
              {partnerLogos.map((url, i) => (
                <img
                  key={`${url}-${i}`}
                  src={absoluteUrl(url)}
                  alt={`Partner ${i + 1}`}
                  loading="lazy"
                  data-testid={`footer-partner-${i}`}
                  className="h-8 sm:h-10 md:h-12 w-auto max-w-[120px] sm:max-w-[160px] object-contain opacity-70 hover:opacity-100 transition-opacity duration-300 grayscale hover:grayscale-0"
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="border-t border-border">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-6 lg:px-12 py-5 sm:py-6 flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-3 text-center text-[0.62rem] sm:text-[0.72rem] uppercase tracking-[0.16em] sm:tracking-[0.22em] text-muted-foreground">
          <div data-testid="footer-copyright" className="order-1 sm:flex-1 sm:text-left">
            © {new Date().getFullYear()} {copyright}
          </div>
          {(footer.group_logo || footer.group_text) && (
            <div
              data-testid="footer-group"
              className="order-3 sm:order-2 flex items-center justify-center gap-2 text-foreground/70"
            >
              {(() => {
                const content = footer.group_logo ? (
                  <img
                    src={absoluteUrl(footer.group_logo)}
                    alt={footer.group_text || "Group"}
                    data-testid="footer-group-logo"
                    className="h-6 md:h-7 w-auto max-w-[140px] object-contain opacity-80 hover:opacity-100 transition-opacity"
                  />
                ) : (
                  <span data-testid="footer-group-text" className="normal-case tracking-[0.15em]">
                    {footer.group_text}
                  </span>
                );
                return footer.group_url ? (
                  <a
                    href={footer.group_url}
                    target="_blank"
                    rel="noreferrer noopener"
                    data-testid="footer-group-link"
                    className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                    aria-label={footer.group_text || "Group site"}
                  >
                    {content}
                  </a>
                ) : (
                  content
                );
              })()}
            </div>
          )}
          <div
            data-testid="footer-tagline"
            className="order-2 sm:order-3 text-primary sm:flex-1 sm:text-right"
          >
            {tagline}
          </div>
        </div>
      </div>
    </footer>
  );
}
